import * as React from "react";
import EnterInfoScreen from "../../../components/layout/signup/EnterInfoScreen";
import Button from "../../../components/atoms/button";
import AppBar from "../../../components/atoms/appbar";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import type { UpdateInput } from "../../../types/auth.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateSchema } from "../../../lib/schemas/auth.schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ConfirmOTPRequest } from "../../../@apis/dto/request/auth.request";
import { updateConfirmOtp } from "../../../@apis/v1/auth.service";
import type { OnErrorResponse } from "../../../@apis/dto/response/common.response";
import { useModalStore } from "../../../lib/store/useModalStore";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";
import type { AuthCertificationResponse } from "../../../@apis/dto/response/auth.response";

export default function SettingInfoEdit() {
  const navigate = useNavigate();
  const { openModal } = useModalStore();
  const { myInfo } = useMyInfoStore();
  const queryClient = useQueryClient();

  const initialCertification: AuthCertificationResponse = {
    requestNo: "",
    resUniqId: "",
    tokenVersionId: "",
    siteCode: "",
    reqDtim: "",
    tokenVal: "",
    reqNo: "",
  };

  const [certification, setCertification] =
    React.useState<AuthCertificationResponse>(initialCertification);
  // 본인인증(OTP) 요청 완료 여부
  const [otpRequested, setOtpRequested] = React.useState(false);
  // alert 확인 시 이동/강조할 입력란
  const [focusTarget, setFocusTarget] = React.useState<string | null>(null);
  // 성별/내·외국인은 EnterInfoScreen 내부 상태이므로 변경 비교를 위해 끌어올림.
  // 초기값은 항상 남성/내국인 (최종 선택값을 myInfo와 비교)
  const [gender, setGender] = React.useState<"male" | "female">("male");
  const [nat, setNat] = React.useState<"domestic" | "foreign">("domestic");

  const handleCertification = (item: AuthCertificationResponse) => {
    setCertification(item);
    setOtpRequested(true);
  };

  const methods = useForm<UpdateInput>({
    resolver: zodResolver(updateSchema),
  });
  const { watch, resetField } = methods;
  const values = watch();

  // 필수 입력 검증: 화면 상단부터 순서대로 첫 번째 미입력 항목을 반환
  const getMissingField = (): { message: string; field: string } | null => {
    const [name, birthdate, mobileno, otp] = watch([
      "name",
      "birthdate",
      "mobileno",
      "otp",
    ]);

    if (!name) return { message: "이름을 입력해 주세요.", field: "name" };
    if ((birthdate?.length ?? 0) !== 6)
      return { message: "생년월일을 입력해 주세요.", field: "birthdate" };
    if ((mobileno?.length ?? 0) < 10)
      return { message: "휴대전화번호를 입력해 주세요.", field: "mobileno" };
    if (!otpRequested)
      return { message: "본인인증을 진행해 주세요.", field: "certification" };
    if ((otp?.length ?? 0) !== 6)
      return { message: "인증번호를 입력해 주세요.", field: "otp" };

    return null;
  };

  const handleSubmit = () => {
    const missing = getMissingField();
    if (missing) {
      openModal("ALERT_DIALOG", {
        title: "알림",
        content: missing.message,
        // 확인 클릭 시 해당 입력란으로 스크롤 이동 및 강조
        onConfirm: () => setFocusTarget(missing.field),
      });
      return;
    }

    // 입력한 정보가 기존 정보와 모두 동일하면 수정 불가
    const sameGender = (gender === "female") === (String(myInfo.gender) === "0");
    const sameNat = nat === myInfo.nat;
    const isUnchanged =
      values.name === myInfo.name &&
      values.birthdate === myInfo.birthdate &&
      values.mobileno === myInfo.mobileno &&
      values.mobileco === myInfo.mobileco &&
      sameGender &&
      sameNat;
    if (isUnchanged) {
      openModal("ALERT_DIALOG", {
        title: "회원 정보 수정 실패",
        content: "변경된 정보가 없습니다.",
      });
      return;
    }

    confirmOTPMutation.mutate({
      ...certification,
      otp: values.otp,
    });
  };

  // 인증번호 불일치 등 인증 실패 처리: alert 확인 시 인증번호 입력란으로
  // 스크롤 이동 및 강조하고, 기존 입력한 인증번호는 초기화한다.
  const handleOtpFail = (content: string) => {
    openModal("ALERT_DIALOG", {
      title: "인증 실패",
      content,
      onConfirm: () => {
        resetField("otp");
        setFocusTarget("otp");
      },
    });
  };

  const confirmOTPMutation = useMutation({
    mutationFn: (form: ConfirmOTPRequest) => updateConfirmOtp(form),
    onSuccess: (res) => {
      const { success } = res;
      if (success) {
        // 수정 완료 → 내 정보 화면에서 최신 정보가 보이도록 갱신
        queryClient.invalidateQueries({ queryKey: ["auth-my-info"] });
        openModal("ALERT_DIALOG", {
          title: "회원 정보 수정 완료",
          content: "회원 정보가 변경되었습니다.",
          onConfirm: () => navigate(-1),
        });
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      handleOtpFail(
        error?.code === "S010"
          ? "인증번호가 일치하지 않습니다.\n인증번호 확인 후 다시 입력해 주세요."
          : "인증번호 확인 중 오류가 발생하였습니다.\n잠시후 다시 시도해주세요.",
      );
    },
  });

  return (
    <div className="flex flex-col">
      <AppBar title="내정보 수정" />
      <section className="px-[16px] pb-[16px] flex flex-col justify-between min-h-[calc(100vh-56px)]">
        <div className="flex-1">
          <FormProvider {...methods}>
            <EnterInfoScreen
              onCertification={handleCertification}
              otpRequested={otpRequested}
              focusTarget={focusTarget}
              onFocusHandled={() => setFocusTarget(null)}
              onGenderChange={setGender}
              onNatChange={setNat}
            />
          </FormProvider>
        </div>

        <div className="flex-row w-full gap-[10px]">
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={confirmOTPMutation.isPending}
            variant={confirmOTPMutation.isPending ? "disabled" : "black"}
            label="수정 완료"
          />
        </div>
      </section>
    </div>
  );
}
